import { ORIGINAL_TYPES, liveTypes, type IssueType } from './issue-types';
/**
 * Telling us what you found, from inside the thing you found it in.
 *
 * WHY A BUTTON AND NOT "RAISE AN ISSUE ON THE FEEDBACK BOARD"
 *
 * That is what testers were asked to do, and it asks a great deal: switch
 * project, remember which one, work out that a bug report is an "issue", pick a
 * priority and an epic for a product they saw ten minutes ago, and write the
 * three things that make a report useful without being told what they are.
 * Every one of those is a reason to shrug and say nothing, and the reports that
 * do arrive say "the button didn't work" with no way to find out which button.
 *
 * So: three boxes, in the words somebody would use anyway, filed for them.
 *
 * WHAT THIS FILE IS AND IS NOT
 *
 * Only the decisions — where it goes, whether to offer it, what gets written.
 * No React and no database, so every rule below is provable without either.
 */
import type { Issue, IssueStatus } from './types';
import type { Project } from './projects';

/**
 * The board feedback lands on.
 *
 * Feedback about FlightDeck belongs on FlightDeck's own board, which is the
 * whole point: the product tracks what is being built and what is holding it
 * up, and what a tester found is exactly that. It is not a special store of
 * comments somewhere off to the side.
 *
 * The document id stays `feedback` — what changed is its NAME. Renaming a
 * document id means moving every issue under it, and nothing is gained: the id
 * appears nowhere anybody reads.
 */
export const FEEDBACK_PROJECT_ID = 'feedback';

/** What that project has to be called for the button to make sense. */
export const FEEDBACK_PROJECT_NAME = 'FlightDeck';

/**
 * The projects a tester is invited to press everything in.
 *
 * Named rather than flagged, because it is two documents that exist only on the
 * test copy and a field on every project would have to be set, migrated and
 * explained for no other purpose.
 */
export const SANDBOX_PROJECT_IDS = ['sandbox'];

/**
 * WHETHER TO OFFER THE BUTTON AT ALL.
 *
 * IN THE SANDBOX, which is where somebody trying the software is working and
 * where they were asked to press everything. Also on the feedback board itself:
 * finding a fault while writing up a fault is not rare, and a button that
 * disappears exactly there would be a small joke at the tester's expense.
 *
 * THE BOARD NO LONGER HAS TO BE HERE — changed 12 August, and it is the whole
 * point of the change. The rule used to be "only where a FlightDeck board
 * exists in this workspace", which was right when everybody shared one. With
 * tenants it hid the button from every tester in their own company: the people
 * whose reports the test round exists to collect were the only ones with no way
 * to say anything. A report now goes through a server into our workspace — see
 * supabase/functions/feedback — so there is always somewhere for it to land.
 *
 * NOT TO A VIEWER — dropped too, for the same reason. That was about the local
 * database refusing an issue below reviewer, and the report is no longer
 * written by the person sending it. A tester with the lowest standing in their
 * own company can still tell us their board is broken, which is exactly who
 * needs to.
 *
 * WHERE IT IS OFFERED, still: in the sandbox, and on the feedback board itself
 * — finding a fault while writing one up is not rare, and a button that
 * disappeared exactly there would be a small joke at the tester's expense. Plus
 * ANY board in a workspace that has no FlightDeck board of its own, which is
 * every customer: they have no sandbox either, so a rule about named boards
 * would offer it nowhere.
 */
export function offersFeedback(opts: {
  projectId: string;
  projects: Pick<Project, 'id'>[];
}): boolean {
  const { projectId, projects } = opts;
  // Somebody in their own company: every board, because there is no sandbox
  // there to single out and nowhere else they would think to look.
  if (!projects.some((p) => p.id === FEEDBACK_PROJECT_ID)) return true;
  return [...SANDBOX_PROJECT_IDS, FEEDBACK_PROJECT_ID].includes(projectId);
}

/** The kinds of thing somebody has to say, in their words rather than ours. */
export const FEEDBACK_KINDS = [
  {
    id: 'broken',
    label: 'Something is broken',
    blurb: 'It did not do what it should, or it did nothing at all.',
    /* The MEANING, resolved to this board's type when the card is made — see
       `feedbackIssue`. Fixed here, it would raise a card as `bug` on a board
       that has no such type and the foreign key in 0072 would refuse it. */
    means: 'fixing' as const,
    priority: 'high' as const,
  },
  {
    id: 'confusing',
    label: 'Something is confusing',
    blurb: 'It works, but I could not tell what it would do or where to go.',
    means: 'upkeep' as const,
    priority: 'medium' as const,
  },
  {
    id: 'idea',
    label: 'An idea',
    blurb: 'It would be better if it did this instead.',
    means: 'upkeep' as const,
    priority: 'medium' as const,
  },
] as const;

export type FeedbackKindId = (typeof FEEDBACK_KINDS)[number]['id'];

export interface FeedbackDraft {
  kindId: FeedbackKindId;
  /** One line anybody could understand. Becomes the title. */
  summary: string;
  doing: string;
  expected: string;
  happened: string;
}

/**
 * WHAT HAS TO BE FILLED IN, WHICH IS ALMOST NOTHING.
 *
 * A summary, and that is the whole requirement. Every extra required box is a
 * report that does not get sent, and half a report about a real fault beats a
 * complete one about nothing. The three questions are asked because being asked
 * is what makes people answer them — not because an answer is demanded.
 */
export function feedbackProblem(draft: FeedbackDraft): string {
  if (!draft.summary.trim()) return 'Say in one line what happened. Everything else is optional.';
  if (draft.summary.trim().length > 120)
    return 'That is long for a title — put the detail in the boxes below.';
  return '';
}

/** Where they were when they pressed the button, which they should not have to know. */
export interface FeedbackContext {
  /** The project open at the time, by name. */
  projectName: string;
  /** The screen they were on. */
  view: string;
  /** Version and commit, so a report can be pinned to exact code. */
  build: string;
  /** live, uat or dev. */
  env: string;
}

/**
 * THE REPORT ITSELF.
 *
 * The three questions are written into the notes as headings whether or not
 * they were answered, and an unanswered one says so. A blank where an answer
 * should be reads as an oversight by whoever wrote the report; "not said" is
 * accurate and takes the blame off them.
 *
 * The context goes at the bottom, in the notes rather than in a field nobody
 * looks at, because the single most common thing missing from a bug report is
 * which version it happened on.
 */
export function feedbackNotes(draft: FeedbackDraft, ctx: FeedbackContext): string {
  const said = (s: string) => (s.trim() ? s.trim() : '*not said*');
  return [
    '**What I was doing:** ' + said(draft.doing),
    '',
    '**What I expected:** ' + said(draft.expected),
    '',
    '**What happened instead:** ' + said(draft.happened),
    '',
    '---',
    '',
    `Reported from **${ctx.projectName}**, on the ${ctx.view} screen.`,
    `Build ${ctx.build} · ${ctx.env}`,
  ].join('\n');
}

/**
 * The issue that gets written, minus the parts only the database can supply —
 * its id, its reference number and its dates.
 *
 * IT LANDS IN THE BACKLOG, not the board's first column. Feedback is a report,
 * not a commitment to do it, and a stream of them dropped into To do would push
 * planned work off the bottom of the screen and make the board useless — which
 * is a fast way to stop reading feedback.
 */
export function feedbackIssue(
  draft: FeedbackDraft,
  ctx: FeedbackContext,
  who: string,
  /* The board's own types — #302. Defaulted to the four originals so every
     existing caller and every check keeps working unchanged. */
  types: IssueType[] = ORIGINAL_TYPES
): Omit<Issue, 'id' | 'ref' | 'createdAt' | 'updatedAt' | 'comments'> {
  const kind = FEEDBACK_KINDS.find((k) => k.id === draft.kindId) ?? FEEDBACK_KINDS[0];
  /*
   * ── WHAT THIS BOARD CALLS THAT — #302 ────────────────────────────────────
   *
   * "Something is broken" always meant a bug, and this said `kind: 'bug'`. On a
   * board that renamed its types there is no such slug, and migration 0072's
   * foreign key would refuse the card outright — so the one screen a customer
   * uses to tell us something is broken would itself be broken.
   *
   * The meaning is fixed; the type is whichever live one this board says counts
   * as that. Falling back to the first live type rather than to a word, because
   * a word is exactly what may not exist.
   */
  const live = liveTypes(types);
  const mine = live.find((t) => t.means === kind.means) ?? live[0] ?? ORIGINAL_TYPES[0];
  return {
    kind: mine.slug,
    title: draft.summary.trim(),
    summary: `${kind.label}, reported by ${who}.`,
    description: feedbackNotes(draft, ctx),
    stands: '',
    status: 'backlog' as IssueStatus,
    priority: kind.priority,
    // One epic, so every report can be found together and none of them has to
    // be filed into a scheme the person reporting has never seen.
    epic: 'Feedback',
    labels: [],
    assignee: '',
    size: '',
    checks: {},
    blocked: '',
    blockerType: '',
    blockerTarget: '',
    activity: [],
  };
}

/** What to say once it has landed, naming the reference so it can be followed. */
export function feedbackThanks(ref: string): string {
  return `Thank you — that is now ${ref} on the ${FEEDBACK_PROJECT_NAME} board.`;
}
